import { maybe, Maybe } from '@passionware/monads';
import type { CustomField, CustomFieldDefinition } from '__types__';
import { isEmpty } from 'lodash';
import { UnitValue } from './UnitField';

export const MEASUREMENT_UNIT_OPTIONS = ['cm', 'm', 'ft', 'in'] as const;

function isMeasurementUnit(
  unit: string | null | undefined
): unit is UnitValue['unit'] {
  return (
    unit != null &&
    (MEASUREMENT_UNIT_OPTIONS as readonly string[]).includes(unit)
  );
}

function formatNumber(num: number): string {
  return parseFloat(num.toFixed(2)).toString();
}

function formatInches(num: number): string {
  // Handle floating-point precision errors more carefully
  // Round to 6 decimal places to remove floating-point errors while preserving meaningful precision
  const rounded = Math.round(num * 1000000) / 1000000;
  return rounded.toString();
}

function numberToString(num: number): string {
  return num.toString();
}

export const unitFieldUtils = {
  /**
   * Convert unit value from system (imperial) to given unit
   * @param value value in feet, should be converted to given unit
   * @param unit target unit to convert to
   */
  getUnitValueFromImperial(
    value: Maybe<number>,
    unit: UnitValue['unit']
  ): UnitValue {
    if (maybe.isAbsent(value)) {
      switch (unit) {
        case 'cm':
        case 'm':
        case 'in':
          return { unit, amount: '' };
        case 'ft':
          return { unit, feet: '', inches: '' };
      }
    }
    if (isNaN(value) || !isFinite(value)) {
      return unitFieldUtils.getUnitValueFromImperial(0, unit);
    }

    switch (unit) {
      case 'cm': {
        const amount = formatNumber(value * 30.48); // 1 ft = 30.48 cm
        return { unit: 'cm', amount };
      }
      case 'm': {
        const amount = formatNumber(value * 0.3048); // 1 ft = 0.3048 m
        return { unit: 'm', amount };
      }
      case 'in': {
        const amount = formatNumber(value * 12); // 1 ft = 12 in
        return { unit: 'in', amount };
      }
      case 'ft': {
        const feet = Math.floor(value);
        const inches = formatInches((value - feet) * 12);
        return { unit: 'ft', feet: feet.toString(), inches };
      }
      default:
        throw new Error(`Unsupported unit: ${unit}`);
    }
  },
  /**
   * Convert unit value from a given metric unit to feet
   * @param value value in the given unit, should be converted to feet
   * @param unit source unit to convert from
   */
  getImperialValueFromUnit(
    value: number,
    unit: UnitValue['unit']
  ): number | null {
    if (isNaN(value) || !isFinite(value)) {
      return 0;
    }
    switch (unit) {
      case 'cm': {
        return value / 30.48;
      }
      case 'm': {
        return value / 0.3048;
      }
      case 'in': {
        return value / 12;
      }
      case 'ft': {
        return value;
      }
      default:
        throw new Error(`Unsupported unit: ${unit}`);
    }
  },
  isEmpty: (value: Maybe<UnitValue>) =>
    maybe.mapOrElse(
      value,
      value => {
        if (value.unit === 'ft') {
          return (
            maybe.isAbsent(maybe.fromTruthy(value.feet)) &&
            maybe.isAbsent(maybe.fromTruthy(value.inches))
          );
        }
        return maybe.isAbsent(maybe.fromTruthy(value.amount));
      },
      false
    ),
  getImperialValueFromUnitValue(value: UnitValue): number | null {
    const parseOrNull = (value: string): number | null => {
      if (value.trim() === '') {
        return null;
      }
      const parsed = parseFloat(value);
      return isNaN(parsed) ? null : parsed;
    };
    switch (value.unit) {
      case 'cm':
      case 'm':
      case 'in': {
        return maybe.mapOrNull(parseOrNull(value.amount), amount =>
          unitFieldUtils.getImperialValueFromUnit(amount, value.unit)
        );
      }
      case 'ft': {
        const feet = parseOrNull(value.feet);
        const inches = parseOrNull(value.inches);
        if (feet === null && inches === null) {
          return null;
        }
        return (feet ?? 0) + (inches ?? 0) / 12;
      }
      default:
        throw new Error('Unsupported unit');
    }
  },
  formatDisplay: (value: UnitValue) => {
    switch (value.unit) {
      case 'ft': {
        const feetDisplay = value.feet || '';
        const inchesDisplay = value.inches || '';
        if (feetDisplay && inchesDisplay) {
          return `${feetDisplay}' ${inchesDisplay}"`;
        } else if (feetDisplay) {
          return `${feetDisplay}'`;
        } else if (inchesDisplay) {
          return `${inchesDisplay}"`;
        }
        return '';
      }
      case 'in':
        return value.amount ? `${value.amount}"` : '';
      case 'cm':
        return value.amount ? `${value.amount}cm` : '';
      case 'm':
        return value.amount ? `${value.amount}m` : '';
      default:
        return '--';
    }
  },
  getUnitValueFromDisplayValue: (
    value: number,
    unit: UnitValue['unit']
  ): UnitValue => {
    switch (unit) {
      case 'ft': {
        const feet = Math.floor(value);
        const inches = formatInches((value - feet) * 12);
        return { unit: 'ft', feet: feet.toString(), inches };
      }
      case 'in': {
        return { unit: 'in', amount: formatNumber(value) };
      }
      case 'cm': {
        return { unit: 'cm', amount: formatNumber(value) };
      }
      case 'm': {
        return { unit: 'm', amount: formatNumber(value) };
      }
      default:
        throw new Error(`Unsupported unit: ${unit}`);
    }
  },
  getUnitValueFromServerValue: (
    value: string,
    unit: UnitValue['unit']
  ): UnitValue => {
    switch (unit) {
      case 'ft': {
        // value is now a string, e.g. "5'6"", "6"", "3'", or just a number as string
        let feet = '';
        let inches = '';
        if (isEmpty(value)) {
          return { unit: 'ft', feet: '', inches: '' };
        }

        // Match compound feet/inches pattern: 5'6"
        const compoundMatch = value.match(/^(\d+)'(\d+)"$/);
        if (compoundMatch) {
          feet = compoundMatch[1];
          inches = compoundMatch[2];
        } else {
          // Match inches only: 6"
          const inchesOnlyMatch = value.match(/^(\d+)"$/);
          if (inchesOnlyMatch) {
            feet = '';
            inches = inchesOnlyMatch[1];
          } else {
            // Match feet only: 3'
            const feetOnlyMatch = value.match(/^(\d+)'$/);
            if (feetOnlyMatch) {
              feet = feetOnlyMatch[1];
              inches = '';
            } else {
              // Try to parse as numeric feet string
              const asNumber = Number(value);
              if (!isNaN(asNumber)) {
                feet = Math.floor(asNumber).toString();
                inches = formatInches((asNumber - Math.floor(asNumber)) * 12);
              }
            }
          }
        }
        return { unit: 'ft', feet, inches };
      }
      case 'in': {
        return { unit: 'in', amount: value ?? '' };
      }
      case 'cm': {
        return { unit: 'cm', amount: value ?? '' };
      }
      case 'm': {
        return { unit: 'm', amount: value ?? '' };
      }
      default:
        throw new Error(`Unsupported unit: ${unit}`);
    }
  },
  /**
   * Convert UnitValue to numeric representation without unit
   * For feet+inches, converts to imperial decimal (total feet)
   * @param value UnitValue to convert
   * @returns numeric value without unit
   */
  getServerValueFromUnitValue: (value: UnitValue): string => {
    switch (value.unit) {
      case 'ft': {
        // Include segments for zeros, but exclude empty strings
        let result = '';
        if (value.feet !== '') {
          result += `${value.feet}'`;
        }
        if (value.inches !== '') {
          result += `${value.inches}"`;
        }
        return result;
      }
      case 'in': {
        return value.amount;
      }
      case 'cm': {
        return value.amount;
      }
      case 'm': {
        return value.amount;
      }
    }
  },
  isUnitValue: (value: unknown): value is UnitValue => {
    if (typeof value !== 'object' || value === null) {
      return false;
    }
    if (
      'unit' in value &&
      'amount' in value &&
      typeof value.unit === 'string' &&
      ['cm', 'm', 'in'].includes(value.unit)
    ) {
      return true;
    }
    return (
      'unit' in value &&
      'feet' in value &&
      'inches' in value &&
      value.unit === 'ft'
    );
  },
  /**
   * Convert UnitValue to numerical value.
   * For feet+inches, converts to inches
   * For inches, converts to inches
   * For cm, converts to cm
   * For m, converts to m
   * @param value UnitValue to convert
   * @returns numerical value, rounded to 0.5 precision to avoid floating point noise when converting back and forth
   */
  getNumericalValue(value: UnitValue): number {
    switch (value.unit) {
      case 'ft': {
        const result = parseFloat(value.feet) * 12 + parseFloat(value.inches);
        return Math.round(result * 2) / 2;
      }
      case 'in': {
        const result = parseFloat(value.amount);
        return Math.round(result * 2) / 2;
      }
      case 'cm': {
        return Math.round(parseFloat(value.amount) * 2) / 2;
      }
      case 'm': {
        return Math.round(parseFloat(value.amount) * 2) / 2;
      }
      default:
        return 0;
    }
  },
  /**
   * Resolves the user-chosen display unit on a MEASUREMENT_NEW custom field row.
   *
   * - V5 saves the chosen unit in `string_value` (cm, m, in, or ft).
   * - Migrated / V1 rows keep canonical `string_value: 'ft'` with the display unit in `raw_unit`.
   *
   * When `string_value` is canonical `ft`, we prefer `raw_unit` for cm/m/in so migrated
   * metric rows display correctly. That matches V1 (`MEASUREMENT_NEW.jsx` reads `raw_unit`).
   * Ambiguity: V5 imperial save with stale `raw_unit` can look the same — fix at save time if needed.
   */
  getMeasurementNewDisplayUnit(
    field: CustomField | null | undefined,
    definition: CustomFieldDefinition
  ): UnitValue['unit'] {
    const defaultUnit = (definition.measurement_default_unit ??
      'ft') as UnitValue['unit'];

    const stringUnit = field?.string_value;
    const rawUnit = field?.raw_unit;

    if (isMeasurementUnit(stringUnit) && stringUnit !== 'ft') {
      return stringUnit;
    }

    if (isMeasurementUnit(rawUnit) && rawUnit !== 'ft') {
      return rawUnit;
    }

    if (stringUnit === 'ft') {
      return 'ft';
    }

    if (isMeasurementUnit(rawUnit)) {
      return rawUnit;
    }

    return defaultUnit;
  },
  /**
   * Formats a MEASUREMENT_NEW custom field for read-only display (table cells, exports).
   * Uses `float_value` with field rounding when present; otherwise rounds via imperial round-trip.
   */
  formatMeasurementNewCustomField(
    field: CustomField | null | undefined,
    definition: CustomFieldDefinition
  ): string | null {
    const unit = unitFieldUtils.getMeasurementNewDisplayUnit(field, definition);

    if (field?.float_value != null) {
      const unitValue = unitFieldUtils.getUnitValueFromImperial(
        field.float_value,
        unit
      );
      const formatted = unitFieldUtils.formatDisplay(unitValue);
      return formatted || null;
    }

    if (field?.raw_value != null && field.raw_value !== '') {
      const parsed = unitFieldUtils.getUnitValueFromServerValue(
        field.raw_value,
        unit
      );
      const imperialValue =
        unitFieldUtils.getImperialValueFromUnitValue(parsed);
      // Round-trip through imperial so raw-only rows get the same rounding as float_value.
      const unitValue = unitFieldUtils.getUnitValueFromImperial(
        imperialValue,
        unit
      );
      const formatted = unitFieldUtils.formatDisplay(unitValue);
      return formatted || null;
    }

    return null;
  }
};
